import enTranslations from './en.json';
import esTranslations from './es.json';
import ptTranslations from './pt.json';

export type SupportedLanguages = 'en' | 'es' | 'br';

function normalizeLanguage(value?: string | null): SupportedLanguages | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase().replace(/_/g, '-');
  const base = normalized.split('-')[0];
  if (base === 'pt') return 'br';
  if (base === 'en' || base === 'es' || base === 'br') return base as SupportedLanguages;
  return null;
}

function extractLanguageFromJwtCookie(): SupportedLanguages | undefined {
  try {
    const cookieStr = document.cookie || '';
    const map = cookieStr.split(/;\s*/).reduce<Record<string, string>>((acc, p) => {
      const i = p.indexOf('=');
      if (i > 0) {
        acc[p.slice(0, i)] = decodeURIComponent(p.slice(i + 1));
      }
      return acc;
    }, {});
    const token = map['ci_user'];
    if (!token) return undefined;
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;
    const mid = parts[1];
    if (!mid) return undefined;
    const payloadJson = atob(mid.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    const lang = normalizeLanguage(payload.language);
    if (lang) return lang;
  } catch {
    /* ignore */
  }
  return undefined;
}

let currentLang: SupportedLanguages | null = null;

const getBrowserLanguage = (): SupportedLanguages => {
  return normalizeLanguage(navigator.language) || 'en';
};

function detectLanguage(): SupportedLanguages {
  if (currentLang) return currentLang;
  try {
    const stored =
      extractLanguageFromJwtCookie() ||
      normalizeLanguage(localStorage.getItem('appLanguage')) ||
      getBrowserLanguage();
    if (stored) {
      currentLang = stored;
      return currentLang;
    }
  } catch {
    /* ignore */
  }
  const nav = typeof navigator !== 'undefined' ? normalizeLanguage(navigator.language) : null;
  currentLang = nav || 'en';
  return currentLang;
}

type Translations = { [key: string]: string | Translations };
const baseTranslations: Record<SupportedLanguages, Translations> = {
  en: enTranslations as Translations,
  es: esTranslations as Translations,
  br: ptTranslations as Translations,
};

function getNested(obj: Translations, path: string): string | undefined {
  const segments = path.split('.');
  let cursor: string | Translations | undefined = obj;
  for (const seg of segments) {
    if (cursor && typeof cursor === 'object' && seg in cursor) {
      cursor = (cursor as Translations)[seg];
    } else {
      return undefined;
    }
  }
  return typeof cursor === 'string' ? cursor : undefined;
}

export function translate(key: string, vars?: Record<string, string | number>): string {
  const lang = detectLanguage();
  const primary = getNested(baseTranslations[lang], key);
  let value: string | undefined = typeof primary === 'string' ? primary : undefined;
  if (!value && lang !== 'en') {
    const fallback = getNested(baseTranslations['en'], key);
    value = typeof fallback === 'string' ? fallback : undefined;
  }
  const finalStr = value || key;
  if (!vars) return finalStr;
  return finalStr.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export default translate;
